/**
 * Organization types and Zod schemas
 *
 * Defines the shape of organizations, members, and projects for the OPaaS feature.
 * These types are used throughout the organization module and related API routes.
 */

import { z } from "zod";
import type { OfferingSupply } from "@/lib/offerings/supplies";
import type { RequestNeed } from "@/lib/requests/needs";
import type { DealPositionView } from "@/lib/deals/types";

// ============================================================================
// Role hierarchy
// ============================================================================

export const ORG_ROLES = ["member", "owner"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

/** Numeric role weight for comparison (higher = more privileged) */
const ROLE_WEIGHT: Record<OrgRole, number> = {
  member: 1,
  owner: 2,
};

/**
 * Check if a role meets the minimum required role level.
 * Role hierarchy: member < owner
 */
export function hasMinRole(userRole: OrgRole, minRole: OrgRole): boolean {
  return ROLE_WEIGHT[userRole] >= ROLE_WEIGHT[minRole];
}

/**
 * Whether a role value represents an organisation owner.
 * Accepts a widened `string` so callers holding a raw DB role
 * (e.g. `AdminOrgMember.role`) don't need to cast.
 */
export function isOwnerRole(role: OrgRole | string | null | undefined): boolean {
  return role === "owner";
}

/**
 * Whether a membership-like record (anything carrying a `role`) is an owner.
 * Use to filter member lists or gate owner-only UI instead of inlining
 * `member.role === "owner"` everywhere.
 */
export function isOwnerMembership(
  membership: { role: OrgRole | string } | null | undefined
): boolean {
  return isOwnerRole(membership?.role);
}

// ============================================================================
// Organization schemas
// ============================================================================

export const OrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  slug: z.string().min(1),
  legal_name: z.string().nullable(),
  address_line1: z.string().nullable(),
  address_line2: z.string().nullable(),
  postal_code: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  vat_id: z.string().nullable(),
  billing_email: z.string().nullable(),
  signatory_name: z.string().nullable(),
  signatory_email: z.string().nullable(),
  logo_url: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(["active", "suspended", "deleted"]),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().uuid().nullable(),
});

export type Organization = z.infer<typeof OrganizationSchema>;

export const UpdateOrganizationSchema = z.object({
  name: z.string().min(1).optional(),
  legal_name: z.string().nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  vat_id: z.string().nullable().optional(),
  billing_email: z.string().email().nullable().optional(),
  signatory_name: z.string().nullable().optional(),
  signatory_email: z.string().email().nullable().optional(),
  logo_url: z.string().url().nullable().optional(),
});

export type UpdateOrganization = z.infer<typeof UpdateOrganizationSchema>;

// ============================================================================
// Organization member schemas
// ============================================================================

export const OrgMemberSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  user_id: z.string().uuid(),
  role: z.enum(["owner", "member"]),
  invited_by: z.string().uuid().nullable(),
  invited_at: z.string().nullable(),
  joined_at: z.string().nullable(),
  status: z.enum(["pending", "active", "deactivated"]),
  deactivated_at: z.string().nullable(),
  deactivated_by: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type OrgMember = z.infer<typeof OrgMemberSchema>;

// ============================================================================
// Organization membership (what the AuthContext carries)
// ============================================================================

export interface OrgMembership {
  id: string;
  name: string;
  slug: string;
  role: OrgRole;
}

// ============================================================================
// Project schemas
// ============================================================================

export const ProjectStatusSchema = z.enum(["draft", "active", "completed", "cancelled"]);
export type ProjectStatus = z.infer<typeof ProjectStatusSchema>;

export const ProjectSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  created_by: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  status: ProjectStatusSchema,
  bundesland: z.string().nullable(),
  landkreis: z.string().nullable(),
  gemeinde: z.string().nullable(),
  plz: z.string().nullable(),
  type: z
    .enum(["okopunkte", "ausgleichsflaechen", "landverpachtung", "co2_zertifikate", "jagdpachten"])
    .nullable(),
  needed_by: z.string().nullable(),
  timing: z.enum(["under_6_months", "over_6_months"]).nullable(),
  naturraum_id: z.string().uuid().nullable(),
  kompensationsraum_id: z.string().uuid().nullable(),
  compensation_instrument_id: z.string().uuid().nullable(),
  import_batch_id: z.string().uuid().nullable(),
  import_row_number: z.number().int().nullable(),
  converted_to_request_id: z.string().uuid().nullable(),
  converted_at: z.string().nullable(),
  converted_by: z.string().uuid().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type Project = z.infer<typeof ProjectSchema>;

// ============================================================================
// Admin-specific types (used by admin API routes and admin UI)
// ============================================================================

/**
 * Organization with aggregated counts for the admin list view.
 */
export interface AdminOrganizationListItem {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
  member_count: number;
  request_count: number;
}

/**
 * Detailed organization data for the admin detail page.
 */
export interface AdminOrganizationDetail {
  id: string;
  name: string;
  slug: string;
  legal_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  vat_id: string | null;
  billing_email: string | null;
  signatory_name: string | null;
  signatory_email: string | null;
  logo_url: string | null;
  notes: string | null;
  status: string;
  assigned_support_agent_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  members: AdminOrgMember[];
  requests: AdminOrgRequest[];
  projects: AdminOrgProject[];
  offerings: AdminOrgOffering[];
  deals: AdminOrgDeal[];
}

export interface AdminOrgMember {
  id: string;
  user_id: string;
  role: string;
  status: string;
  joined_at: string | null;
  invited_at: string | null;
  user: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
  };
}

export interface AdminOrgRequest {
  id: string;
  status: string;
  /** What the request needs: its lines, each in its own unit (lib/requests/needs.ts). */
  lines: RequestNeed[];
  bundesland: string | null;
  created_at: string;
  user: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
}

export interface AdminOrgProject {
  id: string;
  name: string;
  status: string;
  type: string | null;
  bundesland: string | null;
  created_at: string;
  created_by_user: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
}

export interface AdminOrgOffering {
  id: string;
  status: string;
  kreis: string | null;
  landkreis: string | null;
  bundesland: string | null;
  /** What the seller stated, for an Angebot without a live row (lib/offerings/supplies.ts). */
  axis_statement: unknown;
  /** What the Angebot offers: its rows, each in its own unit (lib/offerings/supplies.ts). */
  supplies: OfferingSupply[];
  created_at: string;
}

export interface AdminOrgDeal {
  id: string;
  status: string;
  /** What the deal trades. */
  positions: DealPositionView[];
  offering_id: string | null;
  request_id: string | null;
  seller_id: string | null;
  buyer_id: string | null;
  created_at: string;
}

/**
 * Zod schema for creating an organization via the admin API.
 */
export const CreateOrganizationSchema = z.object({
  name: z.string().min(1, "Name ist erforderlich"),
  owner_email: z.string().email("Gültige E-Mail-Adresse erforderlich"),
  create_user_if_missing: z.boolean().optional().default(false),
});

export type CreateOrganizationInput = z.infer<typeof CreateOrganizationSchema>;

/**
 * Zod schema for updating an organization via the admin API.
 * Extends the tenant-facing schema with admin-only fields (notes, status).
 */
export const AdminUpdateOrganizationSchema = z.object({
  name: z.string().min(1).optional(),
  legal_name: z.string().nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  vat_id: z.string().nullable().optional(),
  billing_email: z.string().email().nullable().optional(),
  signatory_name: z.string().nullable().optional(),
  signatory_email: z.string().email().nullable().optional(),
  notes: z.string().nullable().optional(),
  status: z.enum(["active", "suspended", "deleted"]).optional(),
  /** ST5: per-tenant Ansprechpartner — validated against the support-agent registry in the service. */
  assigned_support_agent_id: z.string().uuid().nullable().optional(),
});

export type AdminUpdateOrganizationInput = z.infer<typeof AdminUpdateOrganizationSchema>;

// ============================================================================
// Service errors
// ============================================================================

export class OrgServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 400
  ) {
    super(message);
    this.name = "OrgServiceError";
  }
}
